import { useState } from 'react';
import Header from '../components/Header.jsx';
import AlphaPad from '../components/AlphaPad.jsx';
import { api } from '../lib/api.js';
import { clearActiveSession } from '../lib/storage.js';
import { roundSignOutHour } from '../lib/datetime.js';

const MIN_LEN = 4;
const MAX_LEN = 10;

// Sessions shorter than this (raw, wall-clock minutes) are too brief to
// register meaningfully — the rounding rules can collapse start and end
// to the same hour (e.g. 09:50 → slot 10, 09:55 sign-out → slot 10).
// We warn before submission and let the pilot decide.
const TOO_SHORT_MIN = 15;

function elapsedMinutes(signin_at_utc) {
  if (!signin_at_utc) return Infinity;
  const t = new Date(signin_at_utc).getTime();
  if (!Number.isFinite(t)) return Infinity;
  return (Date.now() - t) / 60_000;
}

export default function SignOutConfirmScreen({ device, session, onDone, onCancel }) {
  const [cathayId, setCathayId] = useState(session?.cathay_id_entered || '');
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // When the elapsed-time guard triggers we show a modal between the
  // tap and the actual API call. `pendingSignOut` carries the validated
  // payload across the modal so the "End anyway" button doesn't have to
  // re-validate.
  const [pendingSignOut, setPendingSignOut] = useState(null);

  const slotStartHour = session?.signin_rounded_hour
    ? new Date(session.signin_rounded_hour).getHours()
    : null;
  // Slot is always at least 1 hour long. The live end hour expands as
  // the clock crosses each hour boundary (roundSignOutHour treats any
  // minute/second past HH:00 as "slot HH used"), but never collapses
  // to start == end (which would imply a 0-hour session).
  const slotEndHour =
    slotStartHour != null
      ? Math.max(slotStartHour + 1, roundSignOutHour(new Date()))
      : roundSignOutHour(new Date());

  const tooShort = elapsedMinutes(session?.signin_at_utc) < TOO_SHORT_MIN;

  const doSignOut = async (payload) => {
    setBusy(true);
    setErr(null);
    try {
      await api.signout(session.client_uuid, payload);
      clearActiveSession();
      onDone();
    } catch (e) {
      if (e.data?.code === 'clock_skew') {
        setErr('iPad clock is out of sync. Ask your admin to check the date/time.');
      } else {
        setErr(e.message || 'Sign-out failed');
      }
    } finally {
      setBusy(false);
      setPendingSignOut(null);
    }
  };

  const submit = async () => {
    const id = cathayId.trim().toUpperCase();
    if (id.length < MIN_LEN || id.length > MAX_LEN) {
      setErr(`CathayID must be ${MIN_LEN}–${MAX_LEN} letters`);
      return;
    }
    if (!/^[A-Z]+$/.test(id)) {
      setErr('CathayID must contain letters only');
      return;
    }
    const payload = {
      signout_at_utc: new Date().toISOString(),
      cathay_id: id,
    };
    if (tooShort) {
      setPendingSignOut(payload);
      return;
    }
    await doSignOut(payload);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <Header deviceName={device?.name} />
      <main className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl p-8 space-y-6">
          <header className="text-center">
            <h1 className="text-kiosk-lg font-semibold text-cathay-700">Confirm sign out</h1>
            <p className="text-kiosk text-slate-600 mt-2">
              Session will be recorded as slot
            </p>
            <p className="font-mono text-kiosk-lg text-slate-800 mt-1">
              {slotStartHour != null ? String(slotStartHour).padStart(2, '0') : '—'}:00
              {' – '}
              {String(slotEndHour).padStart(2, '0')}:00
            </p>
          </header>

          <div>
            <div className="text-kiosk text-slate-500 text-center">Recording session as</div>
            <div className="bg-slate-100 rounded-2xl px-6 py-5 mt-2 text-center font-mono tracking-[0.3em] text-kiosk-xl text-slate-900">
              {cathayId || <span className="text-slate-300">CathayID</span>}
            </div>
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="mt-3 text-kiosk text-cathay-700 underline w-full text-center"
            >
              {editing ? 'Done editing' : 'Edit CathayID before submitting'}
            </button>
          </div>

          {editing && (
            <AlphaPad
              value={cathayId}
              maxLen={MAX_LEN}
              onKey={(k) => setCathayId((s) => s + k)}
              onBackspace={() => setCathayId((s) => s.slice(0, -1))}
              onClear={() => setCathayId('')}
            />
          )}

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-kiosk">
              {err}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-kiosk-lg font-semibold py-5 rounded-2xl"
            >
              Back
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || cathayId.length < MIN_LEN}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk-lg font-semibold py-5 rounded-2xl shadow"
            >
              {busy ? 'Signing out…' : 'Confirm'}
            </button>
          </div>
        </div>
      </main>

      {pendingSignOut && (
        <div
          className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 space-y-5">
            <h2 className="text-kiosk-lg font-semibold text-amber-700 text-center">
              Session too short
            </h2>
            <p className="text-kiosk text-slate-700">
              Your session will <strong>NOT</strong> be registered in the system, as the
              session is too short (less than {TOO_SHORT_MIN} minutes).
            </p>
            <p className="text-kiosk text-slate-700">
              If you end your session for this time slot now, it will be recorded as
              {' '}
              <strong>NO SHOW</strong>.
            </p>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPendingSignOut(null)}
                disabled={busy}
                className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk font-semibold py-4 rounded-2xl shadow"
              >
                Stay signed in
              </button>
              <button
                type="button"
                onClick={() => doSignOut(pendingSignOut)}
                disabled={busy}
                className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-kiosk font-semibold py-4 rounded-2xl"
              >
                {busy ? 'Signing out…' : 'End anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
